import { ENDPOINTS } from "@constants";
import AbstractService from "./AbstractService";
import NexusService from "./NexusService";
import FeatureFlagService from "./FeatureFlagService";

export default class JurisdictionService extends AbstractService {
	static endpoint = ENDPOINTS.jurisdictions;

	static jurisdictionsApi = NexusService.jurisdictionsApi;

	static jurisdictionsFeature = "use_api_core_endpoints_jurisdiction";

	static getAll({ getCancelToken, ...queryParams }) {
		if(FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			const request = {};
			if (Object.hasOwn(queryParams, "scope")) {
				request.scope = queryParams.scope;
				if (Object.hasOwn(queryParams, "search")) {
					request.search = queryParams.search;
				}
				return this.jurisdictionsApi.lookupJurisdictionByScope(request)
					.then((response) => ({ data: response }));
			}
			if (Object.hasOwn(queryParams, "pageNumber")) {
				request.pageNumber = queryParams.pageNumber;
			}
			if (Object.hasOwn(queryParams, "displayCount")) {
				request.displayCount = queryParams.displayCount;
			}
			if (Object.hasOwn(queryParams, "status")) {
				request.status = queryParams.status;
			}
			if (Object.hasOwn(queryParams, "search")) {
				request.search = queryParams.search;
			}
			return this.jurisdictionsApi.getPaginatedJurisdictionList(request)
				.then((response) => ({ data: response }));
		}

		return this.get({
			endpoint: this.endpoint,
			allowAnonymous: true,
			queryParams,
			getCancelToken,
		});
	}

	static getById(id) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			return this.jurisdictionsApi.getJurisdiction({ id }).then(response => ({ data: response }));
		}

		return this.get({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static updateJurisdictionApproval(id) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			return this.jurisdictionsApi.activateJurisdiction({ id }).then(response => ({ data: response }));
		}

		return this.put({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static deleteJurisdiction(id) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			return this.jurisdictionsApi.deleteJurisdiction({ id }).then(response => ({ data: response }));
		}

		return this.del({
			endpoint: `${this.endpoint}/${id}`,
		});
	}

	static moveJurisdiction(idToBeOverridden, moveToId) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			const getReq = {
				selectedId: moveToId,
				JurisdictionOVerridePayload: {}
			}
			if (idToBeOverridden !== null) {
				getReq.JurisdictionOVerridePayload.IdToBeOverridden = idToBeOverridden;
			}
			return this.jurisdictionsApi.migrateJurisdiction(getReq)
				.then((response) => ({ data: response }));
		}

		return this.put({
			endpoint: `${this.endpoint}/PutOverrideJurisdiction/${moveToId}`,
			data: { idToBeOverridden },
		});
	}

	static create( data ) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			const request = {
				jurisdictionCreatePayload: {},
			};
			if (Object.hasOwn(data, "Name")) {
				request.jurisdictionCreatePayload.Name = data.Name;
			}
			if (Object.hasOwn(data, "DepartmentType")) {
				request.jurisdictionCreatePayload.DepartmentType = data.DepartmentType;
			}
			if (Object.hasOwn(data, "StreetAddress1")) {
				request.jurisdictionCreatePayload.StreetAddress1 = data.StreetAddress1;
			}
			if (Object.hasOwn(data, "City")) {
				request.jurisdictionCreatePayload.City = data.City;
			}
			if (Object.hasOwn(data, "County")) {
				request.jurisdictionCreatePayload.County = data.County;
			}
			if (Object.hasOwn(data, "StateId")) {
				request.jurisdictionCreatePayload.StateId = data.StateId;
			}
			if (Object.hasOwn(data, "Zipcode")) {
				request.jurisdictionCreatePayload.Zipcode = data.Zipcode;
			}

			return this.jurisdictionsApi.createJurisdiction(request)
				.then((response) => ({ data: response }));
		}
		return this.post({
			endpoint: this.endpoint,
			data,
		});
	}

	static edit(data) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			const request = {
				jurisdictionUpdatePayload: {},
			};
			if (Object.hasOwn(data, "Name")) {
				request.jurisdictionUpdatePayload.Name = data.Name;
			}
			if (Object.hasOwn(data, "DepartmentType")) {
				request.jurisdictionUpdatePayload.DepartmentType = data.DepartmentType;
			}
			if (Object.hasOwn(data, "StreetAddress1")) {
				request.jurisdictionUpdatePayload.StreetAddress1 = data.StreetAddress1;
			}
			if (Object.hasOwn(data, "City")) {
				request.jurisdictionUpdatePayload.City = data.City;
			}
			if (Object.hasOwn(data, "County")) {
				request.jurisdictionUpdatePayload.County = data.County;
			}
			if (Object.hasOwn(data, "StateId")) {
				request.jurisdictionUpdatePayload.StateId = data.StateId;
			}
			if (Object.hasOwn(data, "Zipcode")) {
				request.jurisdictionUpdatePayload.Zipcode = data.Zipcode;
			}

			return this.jurisdictionsApi.updateJurisdiction(request)
				.then((response) => ({ data: response }));
		}
		return this.put({
			endpoint: this.endpoint,
			data,
		});
	}

	static verifyJurisdictionIsAvailable(jurisdictionId) {
		if (FeatureFlagService.isEnabled(this.jurisdictionsFeature)) {
			return this.jurisdictionsApi.verifyJurisdictionIsAvailable({ jurisdictionId }).then(response => ({ data: response }));
		}
		return this.get({
			endpoint: `${this.endpoint}/${jurisdictionId}/available`,
			allowAnonymous: true,
		});
	}
}
